import { Card } from "primereact/card";
import { ApiResponse } from "../../services/base/apiResponse";
import { StudentInfoAdminRequest, StudentInfoAdminResponse } from "../../models/dtos";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Link } from "react-router-dom";
import { studentsApi } from "../../services/students/studentsApi";
import { format } from "date-fns";

interface StudentDataProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

const GradeData = ({ student }: StudentDataProps) => {
  const { data: gradesReport } = studentsApi.useGetStudentGradesReportQuery(student?.data?.studentId ?? 0, {
    skip: !student?.data?.studentId,
  });

  const topButtons = (
    <>
      <Link to={`/plans/${student?.data.planId}`}>
        <Button label="Преглед на учебен план" icon="pi pi-eye" severity="info" />
      </Link>
    </>
  );

  const extraButtons = <>{/* <Button label="Експорт CSV" icon="pi pi-upload" severity="secondary" /> */}</>;

  const renderTablesForSemesters = () => {
    if (!student?.data?.studentGrades) {
      return null;
    }

    const sortedGrades = [...student.data.studentGrades].sort((a, b) => {
      const semesterDiff = (a.protocolSemester ?? 0) - (b.protocolSemester ?? 0);
      if (semesterDiff !== 0) return semesterDiff;
      return (a.protocolSubjectName ?? "").localeCompare(b.protocolSubjectName ?? "");
    });

    return (
      <Card title="Оценки">
        <DataTable value={sortedGrades} emptyMessage="Не бяха открити оценки за този студент.">
          <Column field="protocolSemester" header="Семестър" sortable filter></Column>
          <Column field="protocolSubjectName" header="Дисциплина" sortable filter></Column>
          <Column field="protocolTeacherName" header="Преподавател" sortable filter></Column>
          <Column
            field="protocolNumber"
            header="Номер протокол"
            sortable
            filter
            body={(rowData) => (
              <Link to={`/protocols/${rowData.protocolId}`}>
                <Button label={rowData.protocolNumber} severity="info" text />
              </Link>
            )}
          ></Column>
          <Column
            field="protocolIssueDate"
            header="Дата на издаване"
            sortable
            filter
            body={(rowData) =>
              rowData.protocolIssueDate ? format(new Date(rowData.protocolIssueDate), "dd.MM.yyyy") : ""
            }
          ></Column>
          <Column field="studentGradeTypeName" header="Тип оценка" sortable filter></Column>
          <Column
            field="grade"
            header="Оценка"
            sortable
            filter
            body={(rowData) => rowData.grade.toFixed(2)}
            bodyClassName={(rowData) => (rowData.grade <= 2 ? "text-red-500" : "")}
          ></Column>
        </DataTable>
      </Card>
    );
  };

  const renderAverages = () => {
    if (!gradesReport?.data) return null;

    return (
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card>
          <div className="text-lg font-bold">Следване</div>
          <div className="text-2xl">{gradesReport.data.averageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Дипломиране</div>
          <div className="text-2xl">{gradesReport.data.graduationAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Общ среден успех</div>
          <div className="text-2xl">{gradesReport.data.totalAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Текущ семестър</div>
          <div className="text-2xl">{gradesReport.data.currentSemesterAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Предходен семестър</div>
          <div className="text-2xl">{gradesReport.data.previousSemesterAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Предходни два семестъра</div>
          <div className="text-2xl">{gradesReport.data.previousTwoSemestersAverageGrade?.toFixed(2) ?? "N/A"}</div>
        </Card>
        <Card>
          <div className="text-lg font-bold">Невзети изпити</div>
          <div className="text-2xl">{gradesReport.data.notTakenExamsCount ?? "N/A"}</div>
        </Card>
      </div>
    );
  };

  const renderPlanSubjects = () => {
    if (!student?.data?.planSubjects) {
      return null;
    }

    const semesters = Array.from(new Set(student.data.planSubjects.map((subject) => subject.semester))).sort(
      (a, b) => a - b,
    );

    return semesters.map((semester) => {
      const semesterSubjects = student.data.planSubjects.filter((subject) => subject.semester === semester);

      return (
        <Card key={semester} title={`Учебен план - Семестър ${semester}`} className="mt-4">
          <DataTable value={semesterSubjects} emptyMessage={`Не бяха открити дисциплини за семестър ${semester}.`}>
            <Column field="subjectName" header="Дисциплина" sortable filter></Column>
            <Column field="credits" header="Кредити" sortable filter style={{ width: "10%" }}></Column>
            <Column
              field="hours"
              header="Часове"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div>
                  {rowData.lectureHours > 0 && `Л:${rowData.lectureHours} `}
                  {rowData.exerciseHours > 0 && `У:${rowData.exerciseHours} `}
                  {rowData.labHours > 0 && `Лаб:${rowData.labHours} `}
                  {rowData.practiceHours > 0 && `Пр:${rowData.practiceHours}`}
                </div>
              )}
            ></Column>
            <Column
              field="hoursWeekly"
              header="Седмични часове"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div>
                  {rowData.lectureHoursWeekly > 0 && `Л:${rowData.lectureHoursWeekly} `}
                  {rowData.exerciseHoursWeekly > 0 && `У:${rowData.exerciseHoursWeekly} `}
                  {rowData.labHoursWeekly > 0 && `Лаб:${rowData.labHoursWeekly} `}
                  {rowData.practiceHoursWeekly > 0 && `Пр:${rowData.practiceHoursWeekly}`}
                </div>
              )}
            ></Column>
            <Column field="subjectTypeName" header="Тип" sortable filter style={{ width: "10%" }}></Column>
            <Column
              field="subjectControlTypeName"
              header="Форма на контрол"
              sortable
              filter
              style={{ width: "15%" }}
            ></Column>
            <Column
              field="hasPassingGrade"
              header="Взет изпит"
              className="text-center"
              style={{ width: "8%" }}
              body={(rowData) => {
                const maxGrade = student.data.studentGrades
                  ?.filter(
                    (g) => g.protocolSubjectName === rowData.subjectName && g.protocolSemester === rowData.semester,
                  )
                  ?.reduce((max, g) => Math.max(max, g.grade || 0), 0);

                if (maxGrade && maxGrade > 2) {
                  return (
                    <div className="flex align-items-center justify-content-center">
                      <i className="pi pi-check text-green-500 mr-2" />
                      <span className="text-green-500 font-bold">{maxGrade.toFixed(2)}</span>
                    </div>
                  );
                }
                const iconColor = rowData.subjectTypeName === "Свободно избираема" ? "text-yellow-500" : "text-red-500";
                return (
                  <div className="flex align-items-center justify-content-center">
                    <i className={`pi pi-times ${iconColor} mr-2`} />
                    {maxGrade > 0 && <span className={`${iconColor} font-bold`}>{maxGrade.toFixed(2)}</span>}
                  </div>
                );
              }}
            ></Column>
          </DataTable>
        </Card>
      );
    });
  };

  return (
    <div>
      {renderAverages()}
      {renderTablesForSemesters()}
      <Toolbar start={topButtons} end={extraButtons} className="mt-4" />
      {renderPlanSubjects()}
    </div>
  );
};

export default GradeData;
